import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './monitoring';

const setupGlobalImageLoadClasses = () => {
  try {
    if (typeof window === 'undefined') return;
    if (window.__kadolarImgLoadSetup) return;
    window.__kadolarImgLoadSetup = true;

    const ensureImage = (img, { reset = false } = {}) => {
      try {
        if (!(img instanceof HTMLImageElement)) return;
        if (reset) {
          img.classList.remove('kad-img-ready');
          img.classList.remove('kad-img-error');
          try {
            img.dataset.kadImgHooked = '0';
          } catch (_) {}
        }

        const currentSrc = img.currentSrc || img.getAttribute('src') || '';
        const prevSrc = (img.dataset && img.dataset.kadImgSrc) || '';
        if (currentSrc && prevSrc !== currentSrc) {
          try {
            img.dataset.kadImgSrc = currentSrc;
          } catch (_) {}
          img.classList.remove('kad-img-ready');
          img.classList.remove('kad-img-error');
          try {
            img.dataset.kadImgHooked = '0';
          } catch (_) {}
        }

        const hooked = (img.dataset && img.dataset.kadImgHooked) || '0';
        if (hooked === '1') {
          if (img.complete) {
            if (img.naturalWidth > 0) img.classList.add('kad-img-ready');
            else img.classList.add('kad-img-error');
          }
          return;
        }

        try {
          img.dataset.kadImgHooked = '1';
        } catch (_) {}

        const onLoad = () => {
          try {
            img.classList.add('kad-img-ready');
          } catch (_) {}
        };
        const onError = () => {
          try {
            img.classList.add('kad-img-error');
          } catch (_) {}
        };

        img.addEventListener('load', onLoad, { once: true });
        img.addEventListener('error', onError, { once: true });

        if (img.complete) {
          if (img.naturalWidth > 0) onLoad();
          else onError();
        }
      } catch (_) {}
    };

    const scan = (root) => {
      try {
        if (!root) return;
        if (root instanceof HTMLImageElement) {
          ensureImage(root);
          return;
        }
        if (root.querySelectorAll) {
          root.querySelectorAll('img').forEach((img) => ensureImage(img));
        }
      } catch (_) {}
    };

    scan(document);

    const observer = new MutationObserver((mutations) => {
      try {
        for (const m of mutations) {
          if (m.type === 'childList') {
            (m.addedNodes || []).forEach((n) => {
              if (n && n.nodeType === 1) scan(n);
            });
          } else if (m.type === 'attributes') {
            const t = m.target;
            if (t && t.nodeType === 1 && t instanceof HTMLImageElement) {
              ensureImage(t, { reset: true });
            }
          }
        }
      } catch (_) {}
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src', 'srcset'],
    });
  } catch (_) {}
};

setupGlobalImageLoadClasses();

// Suppress benign ResizeObserver loop error overlay in dev
try {
  const roHandler = (e) => {
    const msg = String(e?.message || e?.reason?.message || e?.error?.message || '');
    if (msg.includes('ResizeObserver loop')) {
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      if (e.preventDefault) e.preventDefault();
    }
  };
  window.addEventListener('error', roHandler);
  window.addEventListener('unhandledrejection', roHandler);
  const origErr = console.error;
  console.error = (...args) => {
    const first = String(args[0] || '');
    if (first.includes('ResizeObserver loop')) return;
    return origErr(...args);
  };
} catch {}

const isLocalHost = () => {
  try {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname || '';
    return (
      h === 'localhost' ||
      h === '127.0.0.1' ||
      h === '0.0.0.0' ||
      h.startsWith('10.') ||
      h.startsWith('192.168.') ||
      /^172\.(1[6-9]|2\d|3[0-1])\./.test(h)
    );
  } catch (_) {
    return false;
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

if (process.env.NODE_ENV === 'production') {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      if (isLocalHost()) return;
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  }
} else {
  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => Promise.all(regs.map((r) => r.unregister()))).catch(() => {});
    }
    if (typeof caches !== 'undefined' && caches?.keys) {
      caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).catch(() => {});
    }
  } catch (_) {}
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
